// Components/InfiniteScrollDropdown.tsx

import {
  useState, useRef, useMemo, useId, useCallback,
} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Spin, Input, Radio, Divider, Tooltip } from 'antd';
import styled, { createGlobalStyle } from 'styled-components';
import { greyColor1, maxTablet, primaryColor, primaryFont, secondaryFont, textBlack, wMedium, primaryHeadingColor, maxWidth, retina } from '../../../Utils/utilsStyle';
import { validateSearchQuery } from '../../../Utils/UtilityFunctions';
import SearchGlobalIcon from '../assets/svgIcons/SearchGlobalIcon';
import Cross from '../../globalSearch/assets/Cross';
import PeopleWidgetWithOutHolo from '../components/PeopleWidgetWithOutHolo';

const commonOptionStyle = `
  font-family: ${primaryFont};
  font-size: 10px;
  font-weight: ${wMedium};
  line-height: normal;
`;

const SearchWrapper = styled.div`
  position: relative;
  padding: 8px 16px 0px 16px;
  .search-icon-wrapper {
    gap: 4px;
    .search-global-icon-svg path {
      fill: ${primaryHeadingColor};
    }
    .ant-divider {
      border: 1px solid #DDD;
      margin: 0px;
      top: 0;
    }
  }
  .cross-icon {
    z-index: 1;
    position: absolute;
    right: 25px;
    top: 13px;
  }
  .ant-input-affix-wrapper {
    position: relative;
    border-radius: 18px;
    .ant-input-prefix {
      padding: 0px;
      margin-inline-end: 8px;
      @media all and (max-width: ${maxWidth}), ${retina} {
        padding: 0px;
      }
    }
    input::placeholder {
      color: ${greyColor1} !important;
      font-size: 14px !important;
      font-style: italic !important;
      @media all and (max-width: ${maxTablet}) {
        font-size: 12px !important;
      }
    }
  }
`;

const OptionItemWrapper = styled.div`
  padding: 0 16px;
`;

const OptionItem = styled.div<{ $isActive: boolean }>`
  font-family: ${primaryFont};
  font-size: 16px;
  font-weight: 500;
  color: #67696A;
  letter-spacing: -0.609px;
  padding-bottom: 8px;
  cursor: pointer;
  @media all and (max-width: ${maxTablet}) {
    font-size: 14px;
  }
`;

const OptionLabel = styled.span`
  flex: 1;
  word-break: break-word;
`;

const LoaderWrapper = styled.div<{ isSearchLoading?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.isSearchLoading ? '30px 0px 16px 0px' : '16px 0'};
`;

const OptionsContainer = styled.div<{ maxHeight?: string }>`
  max-height: ${props => props.maxHeight || '287px'};
  overflow-y: auto;
  margin-right: 4px;
  padding: 10px 0px;
  &::-webkit-scrollbar {
    width: 5px;
    border-radius:8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #001760;
    border-radius: 12px;
  }
  &::-webkit-scrollbar-track {
    background: #CFDAE8;
    border-radius: 12px;
  }
  .infinite-scroll-component {
    min-height: 100px;
    position: relative;
    padding-top: 8px;
  }
`;

const OptionCard = styled.div`
  gap: 8px;
  min-width: 0;
  .card-content {
    min-width: 0;
    .card-title {
      color: ${textBlack};
      font-family: ${secondaryFont};
      font-size: 14px;
      font-weight: ${wMedium};
      letter-spacing: -0.277px;
      line-height: normal;
      @media all and (max-width: ${maxTablet}) {
        font-size: 12px;
      }
    }
    .card-designation {
      color: ${primaryHeadingColor};
      ${commonOptionStyle}
      display: block;
      max-width: 100%;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .card-subtitle {
      color: ${primaryColor};
      ${commonOptionStyle}
    }
  }
`;

const TooltipStyles = createGlobalStyle`
  .dropdown-designation-tip {
    .ant-tooltip-inner {
      max-width: 250px;
      white-space: normal;
      font-weight: ${wMedium};
      color: #424b50;
      font-size: 14px;
      font-family: ${primaryFont};
      padding: 10px;
    }
  }
`;

const NoDataFound = styled.div`
  width: 100%;
  height: 80%;
  display: flex;
  align-items: center;
  position: absolute;
  justify-content: center;
  overflow: hidden;
`

export interface OptionType {
  id: number;
  label: string;
  image?: string;
  subtitle?: string;
  personaColor?: string;
  personDesignation?: string;
  [key: string]: any;
}

interface InfiniteScrollDropdownProps {
  options: OptionType[];
  selectedId: number;
  onSelect: (id: number) => void;
  hasSearch?: boolean;
  searchPlaceholder?: string;
  hasMore?: boolean;
  loadMore?: () => void;
  maxHeight?: string;
  isLoading?: boolean;
  isCardOption?: boolean;
  onSearchSubmit?: (term: string) => void;
  useRemoteSearch?: boolean;
  initialSearchTerm?: string;
}

interface DesignationCellProps {
  text: string;
  containerId: string;
}

const DesignationCell: React.FC<DesignationCellProps> = ({ text, containerId }) => {
  const designationRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  const getPopupContainer = useCallback(
    () => document.getElementById(containerId) || document.body,
    [containerId],
  );

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setOpen(false);
      return;
    }
    const el = designationRef.current;
    setOpen(!!el && el.scrollWidth > el.clientWidth);
  };

  return (
    <Tooltip
      trigger="hover"
      placement="bottom"
      open={open}
      onOpenChange={handleOpenChange}
      getPopupContainer={getPopupContainer}
      overlayClassName="dropdown-designation-tip"
      color="#fff"
      title={text}
      arrow={false}
      autoAdjustOverflow={false}
    >
      <span ref={designationRef} className="card-designation">{text}</span>
    </Tooltip>
  );
};

export const InfiniteScrollDropdown: React.FC<InfiniteScrollDropdownProps> = ({
  options,
  selectedId,
  onSelect,
  hasSearch = false,
  searchPlaceholder = "Search...",
  hasMore = false,
  loadMore = () => { },
  maxHeight = "300px",
  isLoading = false,
  isCardOption = false,
  onSearchSubmit,
  useRemoteSearch = false,
  initialSearchTerm = '',
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollId = `scrollable-dropdown-${useId().replace(/:/g, '')}`;

  const filteredOptions = useMemo(
    () => (hasSearch && searchTerm && !useRemoteSearch
      ? options.filter((opt) => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
      : options),
    [hasSearch, searchTerm, useRemoteSearch, options],
  );

  const handleSelect = (id: number) => {

    onSelect(id);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const trimmd = e?.target?.value.trim();
    const isWhitespaceOnly = e?.target?.value.length > 0 && trimmd.length === 0;
    if (isWhitespaceOnly) return;

    const searchQuery = validateSearchQuery(e?.target?.value);
    if (searchQuery.length === 0 && searchTerm.length > 0) {
      clearSearch();
      return;
    }

    setSearchTerm(searchQuery);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearchSubmit?.(searchTerm.trim());
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (useRemoteSearch) {
      onSearchSubmit?.('');
    }
  };

  const renderOptions = () => {
    if (filteredOptions.length > 0) {
      return filteredOptions.map((option) => (
        <OptionItemWrapper key={option.id} className="d-flex flex-column">
          <OptionItem
            className="d-flex align-items-center"
            $isActive={selectedId === option.id}
            onClick={(e) => {
              e.stopPropagation();
              handleSelect(option.id);
            }}
          >
            <Radio checked={selectedId === option.id} onChange={() => handleSelect(option.id)} />
            {isCardOption ? (
              <OptionCard className="d-flex align-items-center">
                <PeopleWidgetWithOutHolo
                  logo={option.image ?? ''}
                  wrapperColor={option.personaColor || '#979797 50%, #979797 50%'}
                  diameter="39px"
                  diameterSm="39px"
                  diameterXsm="39px"
                  ringPadding="2px"
                  ringPaddingSm="2px"
                  margin="0px"
                  marginSm="0px"
                  disableNavigation
                />
                <div className="card-content d-flex flex-column">
                  <span className="card-title">{option.label}</span>
                  {option.personDesignation ? (
                    <DesignationCell
                      text={option.personDesignation}
                      containerId={scrollId}
                    />
                  ) : null}
                  {option.subtitle ? (
                    <span className="card-subtitle">{option.subtitle}</span>
                  ) : null}
                </div>
              </OptionCard>
            ) : (
              <OptionLabel>{option.label}</OptionLabel>
            )}
          </OptionItem>
        </OptionItemWrapper>
      ));
    }
    if (!isLoading) {
      return <NoDataFound>No data found</NoDataFound>;
    }
    if (isLoading) {
      return <LoaderWrapper isSearchLoading={true}><Spin /></LoaderWrapper>;
    }
    return null;
  };

  const optionsList = renderOptions();
  const useInfiniteScroll = hasMore || isCardOption;

  return (
    <>
      <TooltipStyles />
      {hasSearch && (
        <SearchWrapper className='search-wrapper'>
          <Input
            tabIndex={-1}
            className='infinite-dropdown-search'
            size="large"
            placeholder={searchPlaceholder}
            disabled={false}
            prefix={<div className='search-icon-wrapper d-flex align-items-center'><SearchGlobalIcon /><Divider type="vertical" /></div>}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
          />
          <Cross className={`cross-icon search-field-icon ${searchTerm.length > 0 ? '' : 'disabled'}`} onClick={clearSearch} />
        </SearchWrapper>
      )}

      <OptionsContainer
        id={scrollId}
        maxHeight={maxHeight}
        ref={scrollContainerRef}
      >
        {useInfiniteScroll ? (
          <InfiniteScroll
            dataLength={filteredOptions.length}
            next={loadMore}
            hasMore={hasMore && (!searchTerm || useRemoteSearch)}
            loader={(
              <LoaderWrapper isSearchLoading={false}>
                <Spin size="small" />
              </LoaderWrapper>
            )}
            scrollableTarget={scrollId}
          >
            {optionsList}
          </InfiniteScroll>
        ) : optionsList}
      </OptionsContainer>
    </>
  );
};